import { useState, type ChangeEvent } from "react";
import { uploadReceipt } from "../api/documents";
import type { ReceiptUploadResult } from "../types/receipt";

export type ReceiptUploadStage = "idle" | "uploading" | "success" | "error";

export interface ReceiptUpload {
  stage: ReceiptUploadStage;
  error: string | null;
  result: ReceiptUploadResult | null;
  /** A local object URL of the chosen image, shown until the upload succeeds. */
  preview: string | null;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
}

const VALID_TYPES = ["image/jpeg", "image/png", "image/heic", "application/pdf"];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** The checks made before sending a receipt; the sentence of the first failed one. */
function fileProblem(file: File): string | null {
  if (!VALID_TYPES.includes(file.type) && !file.type.startsWith("image/")) {
    return "Please select an image or PDF file.";
  }
  if (file.size > MAX_SIZE_BYTES) return "File is too large. Maximum size is 10 MB.";
  return null;
}

/** One review card's receipt attachment: the file checks, the upload and its stage. */
export function useReceiptUpload(clientId: number): ReceiptUpload {
  const [preview, setPreview] = useState<string | null>(null);
  const [stage, setStage] = useState<ReceiptUploadStage>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReceiptUploadResult | null>(null);

  async function handleChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    const problem = fileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    if (file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
    setStage("uploading");
    setError(null);
    setResult(null);
    try {
      setResult(await uploadReceipt(clientId, file));
      setStage("success");
    } catch (err) {
      setStage("error");
      setError(err instanceof Error ? err.message : "Upload failed");
    }
    event.target.value = "";
  }

  return { stage, error, result, preview, handleChange };
}
